import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FileText, Plus, RefreshCw, Trash2, CheckCircle2, Clock, XCircle, AlertCircle } from 'lucide-react';

const STATUS_BADGES = {
  APPROVED: { icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Approved' },
  PENDING: { icon: Clock, cls: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Pending' },
  REJECTED: { icon: XCircle, cls: 'bg-rose-50 text-rose-700 border-rose-200', label: 'Rejected' },
  DRAFT: { icon: AlertCircle, cls: 'bg-slate-50 text-slate-600 border-slate-200', label: 'Draft' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_BADGES[status] || STATUS_BADGES.DRAFT;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border ${cfg.cls}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function CreateTemplateDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('MARKETING');
  const [language, setLanguage] = useState('en_US');
  const [body, setBody] = useState('Hi {{1}}, thanks for connecting with us!');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setErrors([]);
    try {
      const payload = {
        name,
        category,
        language,
        components: [{ type: 'BODY', text: body }],
      };
      const res = await api.post('/api/templates', payload);
      toast.success(`Template '${res.template.name}' submitted (status: ${res.template.status})`);
      onCreated?.();
      setOpen(false);
      setName('');
      setBody('Hi {{1}}, thanks for connecting with us!');
    } catch (e) {
      const detail = e.body?.detail;
      if (detail?.errors) {
        setErrors(detail.errors);
      } else {
        toast.error(e.message || 'Failed to create template');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-template-btn" className="bg-[#00D1B2] hover:bg-[#00B89C] text-white gap-2">
          <Plus className="w-4 h-4" />
          New Template
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create WhatsApp Template</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Name</Label>
            <Input
              data-testid="tmpl-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="welcome_message"
            />
            <p className="text-xs text-slate-400 mt-1">
              Auto-prefixed with your vendor short-id.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="tmpl-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MARKETING">Marketing</SelectItem>
                  <SelectItem value="UTILITY">Utility</SelectItem>
                  <SelectItem value="AUTHENTICATION">Authentication</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger data-testid="tmpl-lang-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en_US">English (US)</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="hi">Hindi</SelectItem>
                  <SelectItem value="mr">Marathi</SelectItem>
                  <SelectItem value="ta">Tamil</SelectItem>
                  <SelectItem value="te">Telugu</SelectItem>
                  <SelectItem value="kn">Kannada</SelectItem>
                  <SelectItem value="gu">Gujarati</SelectItem>
                  <SelectItem value="bn">Bengali</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Body</Label>
            <Textarea
              data-testid="tmpl-body-input"
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <p className="text-xs text-slate-400 mt-1">
              Use {'{{1}}'}, {'{{2}}'} for variables. No consecutive variables. No URL shorteners.
            </p>
          </div>
          {errors.length > 0 && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-700 space-y-1" data-testid="tmpl-errors">
              {errors.map((e, i) => <div key={i}>• {e}</div>)}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            data-testid="tmpl-submit-btn"
            disabled={submitting || !name || !body}
            onClick={handleSubmit}
            className="bg-[#00D1B2] hover:bg-[#00B89C] text-white"
          >
            {submitting ? 'Submitting…' : 'Submit to Meta'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/templates');
      setTemplates(res.templates || []);
    } catch (e) {
      toast.error(`Could not load templates: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/api/templates/sync');
      toast.success(`Synced: ${res.counts?.synced ?? 0} (approved: ${res.counts?.approved ?? 0})`);
      await load();
    } catch (e) {
      toast.error(`Sync failed: ${e.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    try {
      await api.del(`/api/templates/${id}`);
      toast.success('Template deleted');
      await load();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-8" data-testid="templates-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Templates</h2>
          <p className="text-sm text-slate-500 mt-1">
            Build, validate and submit WhatsApp message templates to Meta.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            variant="outline"
            onClick={handleSync}
            disabled={syncing}
            data-testid="sync-templates-btn"
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            Sync from Meta
          </Button>
          <CreateTemplateDialog onCreated={load} />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid="templates-list">
        {loading ? (
          <div className="p-12 text-center text-sm text-slate-400">Loading…</div>
        ) : templates.length === 0 ? (
          <div className="p-16 text-center">
            <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-sm font-medium text-slate-700">No templates yet</p>
            <p className="text-xs text-slate-400 mt-1">Click "New Template" to create your first one.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {templates.map((t) => (
              <div
                key={t.id}
                data-testid={`tmpl-row-${t.name}`}
                className="flex items-start justify-between gap-4 p-5 hover:bg-slate-50/50"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-semibold text-slate-900 font-mono text-sm">{t.name}</p>
                    <StatusBadge status={t.status} />
                    <span className="text-[11px] text-slate-400 uppercase tracking-wide">
                      {t.category} · {t.language}
                    </span>
                  </div>
                  {t.components?.[0]?.text && (
                    <p className="text-xs text-slate-500 mt-2 line-clamp-2 max-w-2xl">
                      {t.components.find((c) => c.type === 'BODY')?.text}
                    </p>
                  )}
                  {t.rejection_reason && (
                    <p className="text-xs text-rose-600 mt-2">⚠ {t.rejection_reason}</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(t.id)}
                  data-testid={`tmpl-delete-${t.name}`}
                  className="text-slate-400 hover:text-rose-600 shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
